import axios from "axios";

import type { ChangeCategory } from "@shared/changelog/types";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const API_V2_URL = `${API_BASE_URL}/v2`;

// ---------- Types ----------

export interface VersionInfo {
  version: string;
  channel: string;
  buildTime: string;
}

export interface ServiceVersions {
  versions: VersionInfo[];
  latest: string | null;
}

export interface VersionsResponse {
  success: boolean;
  data: Record<string, ServiceVersions>;
}

/**
 * The customer-facing entry a release carries, mirroring the nullable
 * `changelog*` columns on `ReflectorReleases` (CTX-454, plus
 * `changelogActionRequired`). All of them are null on a release that announces
 * nothing, which is normal for an internal patch or a rebuild.
 *
 * Distinct from `notes`, deliberately: `notes` is the technical release note an
 * admin reads while updating reflectors. Sharing an origin does not make them
 * the same text (CTX-456).
 */
export interface ReleaseChangelogFields {
  changelogTitle: string | null;
  changelogDescription: string | null;
  changelogCategory: ChangeCategory | null;
  changelogIsPublic: boolean | null;
  /** One sentence if the customer must act. Null means they need not. */
  changelogActionRequired: string | null;
}

/**
 * Optional on the *read* side, and not as a convenience: the release
 * controller does not select or return these columns yet, so a response today
 * simply has no such keys. Declaring them required would type a field that is
 * `undefined` at runtime as `string | null`, and the edit form would trust it.
 *
 * They become required here when the controller returns them.
 */
export interface ReleaseRecord extends Partial<ReleaseChangelogFields> {
  id: number;
  name: string;
  channel: string;
  scope: string;
  notes: string | null;
  components: Record<string, string>;
  isLatest: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReleasePayload extends ReleaseChangelogFields {
  name: string;
  channel: string;
  notes?: string;
  components: Record<string, string>;
}

// ---------- Version endpoints ----------

/**
 * GET /v2/reflector-admin/versions
 * List all services and their available versions.
 */
export function getVersions(token: string) {
  return axios.get<VersionsResponse>(`${API_V2_URL}/reflector-admin/versions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * GET /v2/reflector-admin/versions/:service
 * List versions for a specific service.
 */
export function getVersionsByService(service: string, token: string) {
  return axios.get(`${API_V2_URL}/reflector-admin/versions/${service}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ---------- Release endpoints ----------

/**
 * GET /v2/reflector-admin/releases
 * List all releases.
 */
export function getReleases(token: string) {
  return axios.get<{ success: boolean; data: ReleaseRecord[] }>(
    `${API_V2_URL}/reflector-admin/releases`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

/**
 * GET /v2/reflector-admin/releases/:id
 * Get a single release by ID.
 */
export function getReleaseById(id: number, token: string) {
  return axios.get<{ success: boolean; data: ReleaseRecord }>(
    `${API_V2_URL}/reflector-admin/releases/${id}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

/**
 * POST /v2/reflector-admin/releases
 * Create a new release.
 */
export function createRelease(data: CreateReleasePayload, token: string) {
  return axios.post(`${API_V2_URL}/reflector-admin/releases`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * PUT /v2/reflector-admin/releases/:id
 * Update an existing release.
 */
export function updateRelease(
  id: number,
  data: Partial<CreateReleasePayload>,
  token: string
) {
  return axios.put(`${API_V2_URL}/reflector-admin/releases/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * DELETE /v2/reflector-admin/releases/:id
 * Delete a release.
 */
export function deleteRelease(id: number, token: string) {
  return axios.delete(`${API_V2_URL}/reflector-admin/releases/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * POST /v2/reflector-admin/releases/:id/set-latest
 * Mark a release as the latest for its channel.
 */
export function setReleaseLatest(id: number, token: string) {
  return axios.post(
    `${API_V2_URL}/reflector-admin/releases/${id}/set-latest`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}
